import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import {
  CloudFrontSigner,
  toCloudFrontSignerParameters,
} from '../../../utils/cloud-front-signer/cloud-front-signer';
import { isDefined } from '../../../utils/variables';
import { Center } from '../../styles/center';
import { useGeodata, UseGeodataModel } from './use-geodata';

export interface GeodataContext {
  geodata: UseGeodataModel;
  signer?: CloudFrontSigner;
  isPending: boolean;
  isGeodataProcessing: boolean;
  refetchGeodata: ReturnType<typeof useQuery>['refetch'];
}

export const GeodataContext = React.createContext<GeodataContext>(undefined!);

type Props = {
  geodataId: string;
  children: React.ReactNode;
};

function useGeodataCloudFrontSigner(geodataId: string) {
  const { data } = useQuery({
    queryKey: ['getGeodataCloudFrontSigner', geodataId],
    queryFn: async () => {
      const result = await SkyMapAxiosServiceFactory.instance
        .createGeodataServiceV0()
        .getSignedRequestParameters({
          path: { geodataId },
        });

      return new CloudFrontSigner(toCloudFrontSignerParameters(result));
    },
    networkMode: 'always',
    retryOnMount: false,
    retry: false,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0,
  });

  const signer = useMemo(() => data, [data]);

  return signer;
}

export const GeodataState = (props: Props) => {
  const { geodata, isPending, errorList, isGeodataProcessing, refetch } = useGeodata(
    props.geodataId,
  );
  const signer = useGeodataCloudFrontSigner(props.geodataId);
  const { t } = useTranslation();

  const contextValue = useMemo<GeodataContext | undefined>(() => {
    if (!isDefined(geodata)) {
      return undefined;
    }

    return { geodata: geodata, isPending, signer, isGeodataProcessing, refetchGeodata: refetch };
  }, [geodata, isPending, signer, isGeodataProcessing, refetch]);

  if (isDefined(errorList)) {
    return <Center>{errorList}</Center>;
  }

  if (!isDefined(contextValue)) {
    return <Center>{t('oneMoment', { ns: 'common' })}</Center>;
  }

  return <GeodataContext.Provider value={contextValue}>{props.children}</GeodataContext.Provider>;
};
